<script setup>
import { reactive, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength } from '@vuelidate/validators';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';

import Button from 'dashboard/components-next/button/Button.vue';

import PlatformTooltipIcon from 'dashboard/components-next/PlatformTooltipIcon.vue';
import {
  parseInstructions,
  mergeInstructions,
} from 'dashboard/components-next/captain/utils/instructionsParser.js';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const isCaptainV2Enabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CAPTAIN_V2)
);

const initialState = {
  handoffMessage: '',
  resolutionMessage: '',
  // Fragmented instruction sections
  personaContext: '',
  agentObjective: '',
  voiceStyle: '',
  instructions: '',
  guardrails: '',
  temperature: 1,
};

const state = reactive({ ...initialState });

// Track which accordion sections are expanded
const expandedSections = reactive({
  personaContext: false,
  agentObjective: false,
  voiceStyle: false,
  instructions: true,
  guardrails: false,
  handoffMessage: false,
  resolutionMessage: false,
});

const validationRules = {
  handoffMessage: { minLength: minLength(1) },
  resolutionMessage: { minLength: minLength(1) },
  instructions: { minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  handoffMessage: getErrorMessage('handoffMessage'),
  resolutionMessage: getErrorMessage('resolutionMessage'),
  instructions: getErrorMessage('instructions'),
}));

/**
 * Token estimation utilities.
 * Average ~4 characters per token (industry heuristic for GPT/Claude family).
 * This is an approximation — actual count varies by model and tokenizer.
 */
const CHARS_PER_TOKEN = 4;

/**
 * Check if a value has meaningful content (non-empty after trim).
 * Unlike stripHtml, this does NOT strip angle brackets, preserving
 * LLM prompt tokens like <persona>, <context>, etc.
 */
const hasContent = val => {
  if (!val) return false;
  return val.trim().length > 0;
};

const sectionStats = computed(() => {
  const result = {};
  const keys = [
    'personaContext', 'agentObjective', 'voiceStyle',
    'instructions', 'guardrails', 'handoffMessage', 'resolutionMessage',
  ];
  keys.forEach(key => {
    const chars = (state[key] || '').trim().length;
    result[key] = {
      chars,
      tokens: chars > 0 ? Math.ceil(chars / CHARS_PER_TOKEN) : 0,
    };
  });
  return result;
});

const totalPromptStats = computed(() => {
  let totalChars = 0;
  let totalTokens = 0;
  Object.values(sectionStats.value).forEach(s => {
    totalChars += s.chars;
    totalTokens += s.tokens;
  });
  return { chars: totalChars, tokens: totalTokens };
});

/**
 * Toggle accordion section expand/collapse.
 */
// --- Textarea auto-resize system ---
const textareaRefs = {};

const registerTextarea = (key, el) => {
  if (el) textareaRefs[key] = el;
};

const autoResize = key => {
  const el = textareaRefs[key];
  if (!el) return;
  // Reset height to recalculate scrollHeight accurately
  el.style.height = 'auto';
  // Set to scrollHeight (full content height) with a small buffer
  el.style.height = `${el.scrollHeight + 2}px`;
};

const resizeAllTextareas = () => {
  nextTick(() => {
    Object.keys(textareaRefs).forEach(key => autoResize(key));
  });
};

const toggleSection = key => {
  expandedSections[key] = !expandedSections[key];
  // When expanding, resize the textarea after DOM update
  if (expandedSections[key]) {
    nextTick(() => autoResize(key));
  }
};

/**
 * Check if a section has content (non-empty after trim).
 */
const sectionHasContent = key => {
  return hasContent(state[key]);
};

/**
 * Get section status text (preenchido / vazio)
 */
const getSectionStatus = key => {
  if (sectionHasContent(key)) {
    return t('CAPTAIN.ASSISTANTS.FORM.SECTION_STATUS.FILLED');
  }
  return t('CAPTAIN.ASSISTANTS.FORM.SECTION_STATUS.EMPTY');
};

// All section definitions for the unified accordion UI (7 sections)
const allSections = computed(() => [
  {
    key: 'personaContext',
    stateKey: 'personaContext',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.PERSONA_CONTEXT.LABEL',
    placeholderKey: 'CAPTAIN.ASSISTANTS.FORM.PERSONA_CONTEXT.PLACEHOLDER',
    tooltipId: 'assistant_persona_context',
    icon: '🎭',
    required: false,
  },
  {
    key: 'agentObjective',
    stateKey: 'agentObjective',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.AGENT_OBJECTIVE.LABEL',
    placeholderKey: 'CAPTAIN.ASSISTANTS.FORM.AGENT_OBJECTIVE.PLACEHOLDER',
    tooltipId: 'assistant_agent_objective',
    icon: '🎯',
    required: false,
  },
  {
    key: 'voiceStyle',
    stateKey: 'voiceStyle',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.VOICE_STYLE.LABEL',
    placeholderKey: 'CAPTAIN.ASSISTANTS.FORM.VOICE_STYLE.PLACEHOLDER',
    tooltipId: 'assistant_voice_style',
    icon: '🎙️',
    required: false,
  },
  {
    key: 'instructions',
    stateKey: 'instructions',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.INSTRUCTIONS.LABEL',
    placeholderKey: 'CAPTAIN.ASSISTANTS.FORM.INSTRUCTIONS.PLACEHOLDER',
    tooltipId: 'assistant_instructions',
    icon: '📝',
    required: true,
    maxLength: 20000,
  },
  {
    key: 'guardrails',
    stateKey: 'guardrails',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.GUARDRAILS_SECTION.LABEL',
    placeholderKey: 'CAPTAIN.ASSISTANTS.FORM.GUARDRAILS_SECTION.PLACEHOLDER',
    tooltipId: 'assistant_guardrails_section',
    icon: '🛡️',
    required: false,
  },
  {
    key: 'handoffMessage',
    stateKey: 'handoffMessage',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.LABEL',
    placeholderKey: 'CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.PLACEHOLDER',
    tooltipId: 'assistant_handoff_message',
    icon: '🔄',
    required: false,
  },
  {
    key: 'resolutionMessage',
    stateKey: 'resolutionMessage',
    labelKey: 'CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.LABEL',
    placeholderKey: 'CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.PLACEHOLDER',
    tooltipId: 'assistant_resolution_message',
    icon: '✅',
    required: false,
  },
]);

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.handoffMessage = config.handoff_message || '';
  state.resolutionMessage = config.resolution_message || '';
  state.temperature = config.temperature || 1;

  // Parse fragmented instructions from the single config.instructions string
  const parsed = parseInstructions(config.instructions);
  state.personaContext = parsed.personaContext;
  state.agentObjective = parsed.agentObjective;
  state.voiceStyle = parsed.voiceStyle;
  state.instructions = parsed.instructions;
  state.guardrails = parsed.guardrails;

  // Auto-expand sections that have content
  Object.keys(expandedSections).forEach(key => {
    if (key !== 'instructions') {
      expandedSections[key] = sectionHasContent(key);
    }
  });
};

const handleSystemMessagesUpdate = async () => {
  const validations = [
    v$.value.handoffMessage.$validate(),
    v$.value.resolutionMessage.$validate(),
  ];

  if (!isCaptainV2Enabled.value) {
    validations.push(v$.value.instructions.$validate());
  }

  const result = await Promise.all(validations).then(results =>
    results.every(Boolean)
  );
  if (!result) return;

  const payload = {
    config: {
      ...props.assistant.config,
      handoff_message: state.handoffMessage,
      resolution_message: state.resolutionMessage,
      temperature: state.temperature || 1,
    },
  };

  if (!isCaptainV2Enabled.value) {
    // Merge the 5 instruction sections back into a single string.
    // Only include sections that have meaningful content.
    const cleanValue = val => {
      return hasContent(val) ? val : '';
    };
    payload.config.instructions = mergeInstructions({
      personaContext: cleanValue(state.personaContext),
      agentObjective: cleanValue(state.agentObjective),
      voiceStyle: cleanValue(state.voiceStyle),
      instructions: cleanValue(state.instructions),
      guardrails: cleanValue(state.guardrails),
    });
  }

  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) {
      updateStateFromAssistant(newAssistant);
      // Resize all textareas after data loads to fit content
      resizeAllTextareas();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- V1: All sections as collapsible accordions -->
    <div v-if="!isCaptainV2Enabled" class="flex flex-col gap-3">
      <!-- Total prompt stats header -->
      <div class="flex items-center justify-between px-3 py-2 rounded-lg bg-n-alpha-2 border border-n-weak">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.PROMPT_STATS.TITLE') }}
        </span>
        <span class="text-sm tabular-nums text-n-slate-11">
          <!-- {{ totalPromptStats.chars.toLocaleString() }} {{ t('CAPTAIN.ASSISTANTS.FORM.PROMPT_STATS.CHARS') }} / -->
          ~{{ totalPromptStats.tokens.toLocaleString() }} tokens
        </span>
      </div>

      <!-- Accordion sections -->
      <div
        v-for="section in allSections"
        :key="section.key"
        class="rounded-xl border border-n-weak overflow-hidden transition-all duration-200"
        :class="{
          'bg-n-solid-2': expandedSections[section.key],
          'bg-n-alpha-1 hover:bg-n-alpha-2': !expandedSections[section.key],
        }"
      >
        <!-- Accordion Header -->
        <button
          type="button"
          class="flex items-center justify-between w-full px-4 py-3 text-left cursor-pointer select-none"
          @click="toggleSection(section.key)"
        >
          <div class="flex items-center gap-2">
            <span class="text-base">{{ section.icon }}</span>
            <span class="text-sm font-medium text-n-slate-12">
              {{ t(section.labelKey) }}
            </span>
            <PlatformTooltipIcon
              :identifier="section.tooltipId"
              size="sm"
            />
            <span
              v-if="section.required"
              class="text-xs text-ruby-11 font-medium"
            >
              *
            </span>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="sectionStats[section.stateKey]?.tokens > 0"
              class="text-xs tabular-nums text-n-slate-10"
            >
              ~{{ sectionStats[section.stateKey].tokens.toLocaleString() }} tokens
            </span>
            <span
              class="text-xs px-2 py-0.5 rounded-full"
              :class="{
                'bg-n-teal-3 text-n-teal-11': sectionHasContent(section.stateKey),
                'bg-n-alpha-3 text-n-slate-10': !sectionHasContent(section.stateKey),
              }"
            >
              {{ getSectionStatus(section.stateKey) }}
            </span>
            <span
              class="text-n-slate-10 transition-transform duration-200 text-sm"
              :class="{ 'rotate-180': expandedSections[section.key] }"
            >
              ▾
            </span>
          </div>
        </button>

        <!-- Accordion Content -->
        <div
          v-show="expandedSections[section.key]"
          class="px-4 pb-4"
        >
          <textarea
            :ref="el => registerTextarea(section.key, el)"
            v-model="state[section.stateKey]"
            :placeholder="t(section.placeholderKey)"
            :maxlength="section.maxLength || undefined"
            rows="5"
            class="w-full p-3 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12 text-sm leading-relaxed resize-y outline-none transition-colors duration-200 focus:border-n-brand hover:border-n-slate-6 placeholder:text-n-slate-11 font-mono prompt-textarea"
            @input="autoResize(section.key)"
          />
          <p
            v-if="formErrors[section.stateKey]"
            class="text-xs text-n-ruby-9 mt-1"
          >
            {{ formErrors[section.stateKey] }}
          </p>
        </div>
      </div>
    </div>

    <!-- Temperature slider (outside accordion) -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.LABEL') }}
      </label>
      <div class="flex items-center gap-4">
        <input
          v-model="state.temperature"
          type="range"
          min="0"
          max="1"
          step="0.1"
          class="w-full"
        />
        <span class="text-sm text-n-slate-12">{{ state.temperature }}</span>
      </div>
      <p class="text-sm text-n-slate-11 italic">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.DESCRIPTION') }}
      </p>
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSystemMessagesUpdate"
      />
    </div>
  </div>
</template>

<style scoped>
.prompt-textarea {
  /* Allow textarea to shrink below rows default when auto-resizing */
  min-height: 120px;
  /* Disable browser scrollbar — height is managed by JS autoResize */
  overflow-y: hidden;
}
</style>
